import { ref, type Ref } from 'vue';
import type { TableElement, TableCell } from '../model/form.types';
import { useTableEditorStore } from '../model/table-editor.store';
import { TABLE_CONSTRAINTS } from '../lib/table-constants';
import { isFractionalColumnWidths } from '../lib/table-column-utils';
import type { ResizeState } from './useTableResize';

export function useTableRowHeights(
  tableElement: Ref<TableElement>,
  gridRef: Ref<HTMLElement | null>,
  resizing?: Ref<ResizeState | null>
) {
  const actualRowHeights = ref<number[]>([]);
  const tableEditorStore = useTableEditorStore();

  const processMergedCellHeight = (cell: HTMLElement, cellData: TableCell, heights: number[]): number => {
    const mergedHeight = cell.offsetHeight;
    const heightPerRow = mergedHeight / cellData.rowSpan;
    for (let j = 0; j < cellData.rowSpan; j++) {
      heights.push(heightPerRow);
    }
    return cellData.rowSpan - 1;
  };

  const findMergedRootInRow = (
    rowIndex: number,
    cellsPerRow: number,
    cells: NodeListOf<Element>,
    cellsData: TableCell[][]
  ) => {
    for (let col = 0; col < cellsPerRow; col++) {
      const cellIndex = rowIndex * cellsPerRow + col;
      const cell = cells[cellIndex] as HTMLElement;
      const cellData = cellsData[rowIndex]?.[col];

      if (cellData?.rowSpan > 1 && !cellData.isMerged) {
        return { cell, cellData };
      }
    }
    return null;
  };

  const processRegularRow = (
    rowIndex: number,
    cellsPerRow: number,
    cells: NodeListOf<Element>,
    heights: number[]
  ) => {
    const cellIndex = rowIndex * cellsPerRow;
    const cell = cells[cellIndex] as HTMLElement;
    if (cell) {
      heights.push(cell.offsetHeight);
    }
  };

  const calculateActualRowHeights = () => {
    if (!gridRef.value) return;

    const storedRowHeights = tableElement.value.properties.rowHeights;
    if (storedRowHeights?.length === tableElement.value.properties.rows) {
      actualRowHeights.value = storedRowHeights;
      return;
    }

    const cellsData = tableElement.value.properties.cells || [];
    const cells = gridRef.value.querySelectorAll('.table-cell');
    const cellsPerRow = tableElement.value.properties.columns;
    const heights: number[] = [];

    for (let i = 0; i < tableElement.value.properties.rows; i++) {
      const mergedRoot = findMergedRootInRow(i, cellsPerRow, cells, cellsData);

      if (mergedRoot) {
        i += processMergedCellHeight(mergedRoot.cell, mergedRoot.cellData, heights);
      } else {
        processRegularRow(i, cellsPerRow, cells, heights);
      }
    }

    if (heights.length === tableElement.value.properties.rows) {
      actualRowHeights.value = heights;
    }
  };

  const findStandardCellForRow = (targetRowIndex: number): { top: string } | null => {
    if (!gridRef.value) return null;

    const standardCells = gridRef.value.querySelectorAll(
      `.table-cell[data-row-index="${targetRowIndex}"]`
    );

    for (const node of Array.from(standardCells)) {
      const cell = node as HTMLElement;
      const rowSpan = cell.getAttribute('aria-rowspan');
      const isStandardRow = !rowSpan || Number.parseInt(rowSpan) === 1;

      if (cell.offsetHeight > 0 && isStandardRow) {
        return { top: `${cell.offsetTop + cell.offsetHeight}px` };
      }
    }

    return null;
  };

  const findTargetCell = (targetRowIndex: number): HTMLElement | null => {
    if (!gridRef.value) return null;

    const mergedRootCell = gridRef.value.querySelector(
      `.table-cell.merged-root[data-row-index="${targetRowIndex}"]`
    ) as HTMLElement | null;

    if (mergedRootCell && mergedRootCell.offsetHeight > 0) {
      return mergedRootCell;
    }

    const visibleCells = gridRef.value.querySelectorAll(
      `.table-cell:not(.hidden)[data-row-index="${targetRowIndex}"]`
    ) as NodeListOf<HTMLElement>;

    for (const cell of Array.from(visibleCells)) {
      if (cell.offsetHeight > 0) {
        return cell;
      }
    }

    return null;
  };

  const getRowHeightsForCalculation = (targetRowIndex: number): number[] | null => {
    if (resizing?.value?.type === 'row' && resizing.value?.currentHeights) {
      return resizing.value.currentHeights;
    }
    if (actualRowHeights.value.length > 0) {
      return actualRowHeights.value;
    }
    return tableElement.value.properties.rowHeights || null;
  };

  const calculateTopForMergedRoot = (targetCell: HTMLElement, targetRowIndex: number): number => {
    let top = targetCell.offsetTop;
    const rowHeights = getRowHeightsForCalculation(targetRowIndex);

    if (rowHeights && rowHeights.length > targetRowIndex && rowHeights[targetRowIndex] > 0) {
      top += rowHeights[targetRowIndex];
      return top;
    }

    const rowSpanAttr = targetCell.getAttribute('aria-rowspan');
    if (rowSpanAttr) {
      const rowSpan = Number.parseInt(rowSpanAttr, 10);
      top += rowSpan > 1 ? targetCell.offsetHeight / rowSpan : targetCell.offsetHeight;
    } else {
      top += targetCell.offsetHeight;
    }

    return top;
  };

  const calculateTopForMergedCell = (targetRowIndex: number): { top: string } | null => {
    if (!gridRef.value) return null;

    const allVisibleCells = Array.from(
      gridRef.value.querySelectorAll('.table-cell:not(.hidden)')
    ) as HTMLElement[];

    for (const cell of allVisibleCells) {
      const cellRow = Number.parseInt(cell.dataset.rowIndex || '-1', 10);
      const rowSpanAttr = cell.getAttribute('aria-rowspan');
      const cellRowSpan = rowSpanAttr ? Number.parseInt(rowSpanAttr, 10) : 1;

      if (cellRow >= 0 && cellRowSpan > 1) {
        const start = cellRow;
        const end = cellRow + cellRowSpan - 1;
        if (start <= targetRowIndex && targetRowIndex < end) {
          const relativeRowIndex = targetRowIndex - start + 1;
          const perRowHeight = cell.offsetHeight / cellRowSpan;
          let top = cell.offsetTop + perRowHeight * relativeRowIndex;

          const maxTop = Math.max(0, gridRef.value.clientHeight - 1);
          top = Math.max(0, Math.min(top, maxTop));

          return { top: `${top}px` };
        }
      }
    }

    return null;
  };

  const calculateOffsetFromHeights = (heights: number[], rowIndex: number): { top: string } => {
    const offset = heights.slice(0, rowIndex).reduce((sum, h) => sum + h, 0);
    return { top: `${offset}px` };
  };

  const calculateTopFromTargetCell = (targetCell: HTMLElement, targetRowIndex: number): number => {
    return targetCell.classList.contains('merged-root')
      ? calculateTopForMergedRoot(targetCell, targetRowIndex)
      : targetCell.offsetTop + targetCell.offsetHeight;
  };

  const tryCalculateFromGridCells = (targetRowIndex: number): { top: string } | null => {
    if (!gridRef.value) return null;

    try {
      const standardCellResult = findStandardCellForRow(targetRowIndex);
      if (standardCellResult) {
        return standardCellResult;
      }

      const targetCell = findTargetCell(targetRowIndex);
      if (targetCell && targetCell.offsetHeight > 0) {
        const top = calculateTopFromTargetCell(targetCell, targetRowIndex);
        return { top: `${top}px` };
      }

      const mergedCellResult = calculateTopForMergedCell(targetRowIndex);
      if (mergedCellResult) {
        return mergedCellResult;
      }
    } catch (e) {
      console.error('Error calculating row resize handle position:', e);
    }

    return null;
  };

  const getFallbackRowHeights = (totalRows: number): number[] => {
    return (
      tableElement.value.properties.rowHeights ||
      new Array<number>(totalRows).fill(TABLE_CONSTRAINTS.DEFAULT_ROW_HEIGHT)
    );
  };

  const getRowResizeHandleStyle = (rowIndex: number) => {
    const targetRowIndex = rowIndex - 1;
    const totalRows = tableElement.value.properties.rows;

    const gridResult = tryCalculateFromGridCells(targetRowIndex);
    if (gridResult) {
      return gridResult;
    }

    if (resizing?.value?.type === 'row') {
      const liveHeights = resizing.value.currentHeights;
      if (liveHeights?.length === totalRows) {
        return calculateOffsetFromHeights(liveHeights, rowIndex);
      }
    }

    if (actualRowHeights.value?.length === totalRows) {
      return calculateOffsetFromHeights(actualRowHeights.value, rowIndex);
    }

    const rowHeights = getFallbackRowHeights(totalRows);
    return calculateOffsetFromHeights(rowHeights, rowIndex);
  };

  const convertFractionalUnitsToPixels = () => {
    if (!gridRef.value) return;

    const columnWidths = tableElement.value.properties.columnWidths;
    if (!columnWidths || columnWidths.length === 0) return;

    const hasFractionalUnits = isFractionalColumnWidths(columnWidths);
    if (!hasFractionalUnits) return;

    const totalWidth = gridRef.value.clientWidth;
    const totalFractions = columnWidths.reduce((sum, w) => sum + w, 0);
    const pixelPerFraction = totalWidth / totalFractions;

    const pixelWidths = columnWidths.map((w) => Math.round(w * pixelPerFraction));

    tableEditorStore.updateTableColumnWidths(tableElement.value.id, pixelWidths);
  };

  return {
    actualRowHeights,
    calculateActualRowHeights,
    getRowResizeHandleStyle,
    convertFractionalUnitsToPixels,
  };
}